import React from 'react';
import { GetUrlToOpenUsingDefault } from 'flora-common';
import {
    ActivityFeed as LazyActivityFeed,
    ActivityFeedTitle as LazyActivityFeedTitle,
} from 'owa-activity-feed';
import { logUsage } from 'owa-analytics';
import { DrawerPane } from 'owa-drawer';
import type { MailboxInfo } from 'owa-client-types';
import { isFeatureEnabled } from 'owa-feature-flags';
import { observer } from 'owa-mobx-react';
import { popoutMailReadingPane } from 'owa-popout';
import closeHeaderPane from '../../orchestration/closeHeaderPane';
import type { OwaHeaderPaneProps } from '../../types/OwaHeaderPaneProps';

interface OpenMailAction {
    openMail: (mailboxInfo: MailboxInfo, messageId: string, itemId?: string) => void;
}

interface OpenLinkAction {
    openLink: (mailboxInfo: MailboxInfo, url: string) => void;
}

const notificationsOpenMailAction: OpenMailAction = {
    openMail: (mailboxInfo, messageId, itemId) => {
        popoutMailReadingPane(
            /* itemId */ itemId,
            /* conversationId */ undefined,
            /* internetMessageId */ messageId,
            /* mailboxInfo */ mailboxInfo
        );
    },
};

const notificationsOpenLinkAction: OpenLinkAction = {
    openLink: async (mailboxInfo, url) => {
        /* eslint-disable-next-line owa-custom-rules/require-undefined-parameter -- (https://aka.ms/OWALintWiki)
         * Flight checks that supply MailboxInfo should be defined as AccountFeatureName value and should be checked using isAccountFeatureEnabled to ensure consistent checking.
         *	> The parameter mailboxInfo must be undefined. Feature flight: 'doc-flora-stage1' */
        const isFloraEnabled = isFeatureEnabled('doc-flora-stage1', mailboxInfo);
        isFloraEnabled ? await openLinkWithFLORA(url) : window.open(url, '_blank');
    },
};

async function openLinkWithFLORA(url: string): Promise<void> {
    const urlToOpen = await GetUrlToOpenUsingDefault(url);

    if (urlToOpen) {
        // OW: 270495 Need to use the main window instead of targetWindow to prevent a dialog from showing in popout's.
        const handle = window.open(urlToOpen, '_self');
        if (!handle) {
            logUsage('FLORAFailedToOpen');
        }
    } else {
        window.open(url, '_blank');
    }
}

const notificationsPaneTitle = () => {
    return <LazyActivityFeedTitle />;
};

export default observer(function NotificationsPane(props: OwaHeaderPaneProps) {
    const handleDismiss = React.useCallback(() => {
        closeHeaderPane(props.paneId);
    }, [props.paneId]);

    return (
        <DrawerPane
            paneId={props.paneId}
            headerText={notificationsPaneTitle()}
            onDismiss={handleDismiss}
        >
            <LazyActivityFeed
                openMailAction={notificationsOpenMailAction}
                openLinkAction={notificationsOpenLinkAction}
            />
        </DrawerPane>
    );
}, 'NotificationsPane');
